import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, FormControlLabel, Radio, Box, TextField } from '@mui/material';
import PreSaleTermsPage from '../PreSaleTermsPage';
import { submitToJotForm } from '../services/api/jotForm';
import { useNotification } from '../context/NotificationContext';

type CheckoutModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  termsHref?: string;
};

const CheckoutModal = ({
  open,
  onClose,
  onConfirm,
  title = 'Before you pay',
  message = 'You are about to be redirected to our secure Stripe checkout. Carefully read the Pre‑Sale Terms and Terms & Conditions.',
  termsHref = '/terms',
}: CheckoutModalProps) => {
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const formatToday = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const todayStr = formatToday();
  const [date, setDate] = useState(todayStr);
  const [submitting, setSubmitting] = useState(false);
  const { showNotification } = useNotification?.() ?? { showNotification: (msg: string) => console.log(msg) } as any;

  const handleClose = () => {
    setAgreeTerms(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setDate('');
    onClose();
  };

  const handleConfirm = async () => {
    const canPay = firstName.trim() && lastName.trim() && email.trim() && date && agreeTerms;
    if (!canPay || submitting) return;
    const checkoutUrl = import.meta.env.VITE_STRIPE_CHECKOUT_URL as string | undefined;
    if (!checkoutUrl) {
      showNotification?.('Checkout link is not configured. Please set VITE_STRIPE_CHECKOUT_URL.', 'error');
      return;
    }
    try {
      setSubmitting(true);
      // Ensure date cannot be altered away from today
      if (date !== todayStr) {
        setDate(todayStr);
      }
      await submitToJotForm({ firstName, lastName, email, date: todayStr });
      showNotification?.('Details saved.', 'success');
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      setAgreeTerms(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setDate(todayStr);
    } catch (err: any) {
      console.error(err);
      showNotification?.(err?.message || 'Failed to save details. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#1A2B4D' }}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#44536B', mb: 2 }}>{message}</Typography>
        {/* Render Pre‑Sale Terms inside the modal with scroll */}
        <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid #EEE', borderRadius: 1, mb: 2, backgroundColor: '#FFFFFF' }}>
          <PreSaleTermsPage />
        </Box>
        {/* Name and Date inputs */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label='First name'
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size='small'
            fullWidth
          />
          <TextField
            label='Last name'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size='small'
            fullWidth
          />
          <TextField
            label='Email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size='small'
            fullWidth
          />
          <TextField
            label='Date'
            type='date'
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size='small'
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayStr, max: todayStr }}
            onKeyDown={(e) => { e.preventDefault(); }}
          />
        </Box>
        {/* Terms acceptance */}
        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Radio checked={agreeTerms} onChange={(_, checked) => setAgreeTerms(checked)} />}
          label={
            <Typography sx={{ color: '#1A2B4D' }}>
              I have read and agree to the{' '}
              <Box component="a" href="/terms" target="_blank" rel="noopener" style={{ color: '#FE6F61', fontWeight: 600, textDecoration: 'none' }}>
                Terms & Conditions
              </Box>
              {' '}and{' '}
              <Box component="a" href="/pre-sale-terms" target="_blank" rel="noopener" style={{ color: '#FE6F61', fontWeight: 600, textDecoration: 'none' }}>
                Pre‑Sale Terms
              </Box>
              .
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant='outlined' disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={submitting || !(firstName.trim() && lastName.trim() && email.trim() && date && agreeTerms)} variant='contained' sx={{ bgcolor: '#FE6F61', '&:hover': { bgcolor: '#e96055' }, color: '#FFFFFF' }}>
          Pay Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutModal;


